/**
 * Schedule overrides layered over the weekly doctor_schedules baseline.
 *
 * Two kinds:
 *   - off          — block the [starts_at, ends_at) window. Subtracts
 *                    from the regular day's open intervals.
 *   - custom_hours — REPLACE the day's open intervals with this window
 *                    (other normal hours for that day are ignored).
 *                    Use case: "Thursday I'll only work 10:00–14:00
 *                    instead of my normal 10–20." Multiple custom_hours
 *                    overrides on the same day are unioned.
 *
 * Both kinds coexist: a 'custom_hours' window can be partially blocked
 * by a later 'off' override on the same day.
 */
import { z } from "zod";

export const scheduleOverrideKindSchema = z.enum(["off", "custom_hours"]);
export type ScheduleOverrideKind = z.infer<typeof scheduleOverrideKindSchema>;

export interface ScheduleOverrideApi {
	id: string;
	clinic_id: string;
	member_id: string;
	kind: ScheduleOverrideKind;
	starts_at: string;
	ends_at: string;
	reason: string | null;
	created_at: string;
	member_name: string | null;
}

export const createScheduleOverrideInputSchema = z
	.object({
		member_id: z.string().uuid(),
		kind: scheduleOverrideKindSchema.default("off"),
		starts_at: z.string().datetime(),
		ends_at: z.string().datetime(),
		reason: z.string().max(200).optional(),
	})
	.refine(
		(v) => new Date(v.ends_at).getTime() > new Date(v.starts_at).getTime(),
		{ message: "ends_at must be after starts_at", path: ["ends_at"] },
	);
export type CreateScheduleOverrideInput = z.infer<
	typeof createScheduleOverrideInputSchema
>;

export const listScheduleOverridesQuerySchema = z.object({
	member_id: z.string().uuid().optional(),
	from: z.string().datetime().optional(),
	to: z.string().datetime().optional(),
});
export type ListScheduleOverridesQuery = z.infer<
	typeof listScheduleOverridesQuerySchema
>;
